import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
  ConfigService,
  DcpApiPlatforms,
  DcpBundleTemplate,
  EndpointsService,
  HttpRequestOptions,
  IConfigService, LocalStorageService
} from '@dcp/configurator';
import {AuthService, Product} from '@spartacus/core';
import {Observable, throwError} from 'rxjs';
import {concatMap, distinctUntilChanged, map, shareReplay} from 'rxjs/operators';
import {CommercialModelConfig} from '../model/dcp-config-service/commercial-model-config';
import {CountryConfig} from '../model/dcp-config-service/country-config';
import {Industry} from '../model/dcp-config-service/industries';
import {SalesOrg, SalesOrgPayload} from '../model/dcp-config-service/sales-org';
import {CountryBpTaxConfig, CountryBpTaxPayload, TaxIdValidationPayload} from '../model/dcp-config-service/tax-config';
import {PlansAndPricingDataService} from '../product/details/plans-and-pricing/plans-and-pricing-data.service';
import {DcpKymaService} from './dcp-kyma.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})

/**
 * This service provides methods for consuming dcp-lambda-config-service
 *
 * https://wiki.wdf.sap.corp/wiki/display/DCPL/dcp-lambda-config-service
 * https://github.tools.sap/DCP/dcp-lambda-config-service
 */
export class DcpConfigService implements IConfigService {
  salesOrgsCache = new Map<string, Observable<SalesOrg>>();
  private commercialModelConfigCache: Map<string, Observable<CommercialModelConfig>> = new Map();
  private storageKey = 'permissionsData';

  constructor(
    private readonly http: HttpClient,
    private readonly kymaService: DcpKymaService,
    private readonly authService: AuthService,
    private localStorageService: LocalStorageService,
    private configService: ConfigService,
    private endpointsService: EndpointsService,
    private plansAndPricingDataService: PlansAndPricingDataService
  ) {
    this.loadPermissionsOnLogin();
  }

  public getSalesOrg(payload: SalesOrgPayload): Observable<SalesOrg> {
    const cacheKey = `${payload.vendorId}-${payload.countryIsoCode}`;
    const paramsData = new HttpParams();
    paramsData.set('Default', `${payload.httpParams.default}`);
    if (this.salesOrgsCache.has(cacheKey)) {
      return this.salesOrgsCache.get(cacheKey);
    }
    const salesOrgObservable = this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) => {
          config.params = paramsData;
          return this.http.get<SalesOrg>(
            `${this.kymaService.getUrl('pdpDecorator')}${this.configService.getString('kyma.config.salesOrg')}/${payload.vendorId}/${payload.countryIsoCode}`,
            config,
          );
        },
      ),
      shareReplay(1)
    );
    this.salesOrgsCache.set(cacheKey, salesOrgObservable);
    return salesOrgObservable;
  }

  public getIndustries(): Observable<Industry[]> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<{ industries: Industry[] }>(
            `${this.kymaService.getUrl('config')}${this.configService.getString('kyma.config.industries')}`,
            config,
          ),
      ),
      map(({industries}) => industries),
    );
  }

  public getCountryConfig(countryIsoCode: string): Observable<CountryConfig> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<CountryConfig>(
            `${this.kymaService.getUrl('config')}${this.configService.getString('kyma.config.countryConfig')}/${countryIsoCode}`,
            config,
          ),
      ),
    );
  }

  public getPermissions(): Observable<CountryConfig> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<CountryConfig>(
            `${this.kymaService.getUrl('config')}${this.configService.getString('kyma.config.permissions')}`,
            config,
          ),
      ),
    );
  }

  /**
   * Loads permissions on login
   *
   * Room for improvement load only once per login if the data will not change during one session.
   */
  public loadPermissionsOnLogin(): void {
    this.authService.isUserLoggedIn()
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((isLoggedIn) => {
        if(isLoggedIn) {
          this.getPermissions().pipe(untilDestroyed(this)).subscribe((permissions) => {
            this.localStorageService.setItem(this.storageKey, JSON.stringify(permissions));
          });
        }
      });
  }

  public getForbiddenWords(): Observable<string[]> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<string[]>(
            `${this.kymaService.getUrl('config')}${this.configService.getString('kyma.config.forbiddenWords')}`,
            config,
          ),
      ),
    );
  }

  /**
   * Gets the cached CommercialModelConfig for a product specified in the COMMERCIAL_MODEL(S) data object
   *
   * @param product legacy or cps product
   * @param transactionTypeCode (optional) if not set returns the default config
   * @param template edition template
   * @return Observable<CommercialModelConfig> or throws an error if no commercialModel is specified in the product
   */
  public getCommercialModelConfig(product: Product, transactionTypeCode?: string, template?: DcpBundleTemplate): Observable<CommercialModelConfig> {
    const productCM = this.plansAndPricingDataService.getCommercialModel(product?.data);
    const bundleCM = template ? this.plansAndPricingDataService.getCommercialModel(template?.data) : this.plansAndPricingDataService.getCommercialModel(product?.bundleTemplates?.[0]?.data);

    const commercialModel = bundleCM || productCM;

    if (!commercialModel?.code) {
      return throwError(`No commercialModel found for product "${product.code}"!`);
    } else {
      return this.getCommercialModelConfigByCode(commercialModel.code, transactionTypeCode);
    }
  }

  public getRequiredTaxIds(countryBpTaxPayload: CountryBpTaxPayload): Observable<CountryBpTaxConfig> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<CountryBpTaxConfig>(
            this.endpointsService.buildUrl(
              DcpApiPlatforms.KYMA,
              'config',
              'requiredTaxIds',
              countryBpTaxPayload
            ),
            config,
          )
      )
    );
  }

  public validateTaxIds(taxValidationPayload: TaxIdValidationPayload): Observable<{}> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: { headers: HttpHeaders; params: {} }) =>
          this.http.post<CountryBpTaxConfig>(
            this.endpointsService.buildUrl(
              DcpApiPlatforms.KYMA,
              'config',
              'validateTaxId'
            ),
            taxValidationPayload,
            config
          )
      )
    );
  }

  private getCommercialModelConfigByCode(commercialModelCode: string, transactionType?: string): Observable<CommercialModelConfig> {
    const cacheKey: string = `${commercialModelCode}.${transactionType}`;
    return this.commercialModelConfigCache.has(cacheKey) ? this.commercialModelConfigCache.get(cacheKey) : this.getCommercialModelConfigFromBackEnd(commercialModelCode, transactionType);
  }

  private getCommercialModelConfigFromBackEnd(commercialModelCode: string, transactionType?: string): Observable<CommercialModelConfig> {
    const config: HttpRequestOptions = this.kymaService.prepareHeaderConfig();

    if (transactionType) {
      if (!config?.params) {
        config.params = new HttpParams();
      }
      config.params = config.params.append('transaction-type', transactionType);
    }

    const commercialModelConfig$: Observable<CommercialModelConfig> = this.http.get<CommercialModelConfig>(
      `${this.kymaService.getUrl('config')}${this.configService.getString('kyma.config.commercialModel')}/${commercialModelCode}`,
      config
    ).pipe(shareReplay(1));

    this.commercialModelConfigCache.set(`${commercialModelCode}.${transactionType}`, commercialModelConfig$);
    return commercialModelConfig$;
  }
}
